import { noop } from 'underscore';
import React, { useCallback, useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { withPageReadyAsync } from '@bingads-webui-react/with-page-ready';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import {
  extractSourceId,
  appendCacheId,
  uetReportConversion,
  gTagReportConversion,
  getLiveChatSrc,
  getPopupWindowFeatures,
  removeQueryParam,
} from '@bingads-webui-campaign-react/pmax-landing-utils';
import { LandingPageHeader, LandingPageFooter } from '@bingads-webui-campaign-react/pmax-landing-header';
import { LandingPageCover, DelayedAdPreviewPlaceholder } from '@bingads-webui-campaign-react/pmax-landing-cover';

const DelayedChatEntrance = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/chat-entrance').then(({ ChatEntrance }) => ({
    default: ChatEntrance,
  })));

const DelayedLandingPageFeature = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/pmax-landing-feature').then(({ LandingPageFeature }) => ({
    default: LandingPageFeature,
  })));

const DelayedLandingPageCost = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/pmax-landing-cost').then(({ LandingPageCost }) => ({
    default: LandingPageCost,
  })));

const DelayedLandingPageFAQ = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/pmax-landing-faq').then(({ LandingPageFAQ }) => ({
    default: LandingPageFAQ,
  })));

function getIndexVersion() {
  const regex = /^[\d|\w]{8}$/;
  const matches = regex.exec(document.lastChild.nodeValue);

  return matches ? matches[0] : '';
}

export function PMaxLiteLandingPageCore(props) {
  const { logger } = props;
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const isSDKMode = isSDK();
  const [cacheId, setCacheId] = useState('');
  const [showAdPreviewPlaceholder, setShowAdPreviewPlaceholder] = useState(true);
  const websiteInputRef = useRef(null);
  const methodsContainerRef = useRef(null);
  const liveChatSrc = getLiveChatSrc();

  const {
    Urls: {
      FederatedLogin: loginUrl,
      PMaxLiteSignup: signUpUrl,
    } = {},
    AssignCouponClassId = '',
    Permissions: {
      isGoogleAuthEnabled,
      isGoogleAuthNewSignInPageEnabled,
      isFacebookAuthEnabled,
      isFacebookAuthNewSignInPageEnabled,
    },
  } = serverContext;

  useEffect(() => {
    const indexVersion = getIndexVersion();
    const scid = extractSourceId();
    const signupScid = extractSourceId(signUpUrl);
    logger.trace(`Launching PMax Lite Landing Page. s_cid: ${scid}. signup_s_cid: ${signupScid}. AssignCouponClassId: ${AssignCouponClassId}. indexVersion: ${indexVersion}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUpUrlWithCacheId = appendCacheId(signUpUrl, cacheId);

  const getOnClickSignUpFn = useCallback(section => (() => {
    uetReportConversion();
    gTagReportConversion();
    if (isGoogleAuthEnabled || isFacebookAuthEnabled) {
      // Go to signup idp selection page
      logger.trace(`[${section}] Click sign up button to go to sign up idp selection page.`);
      const path = `/campaign/pmaxlitelanding/signup${location.search}`;
      const pathWithCacheId = appendCacheId(path, cacheId);
      window.open(pathWithCacheId, '_self');
    } else if (isSDKMode) {
      const signUpUrlWithCacheIdNoSDK = removeQueryParam('isSDK', signUpUrlWithCacheId);
      logger.trace(`[SDK][${section}] Click sign up button. signUpUrl: ${signUpUrlWithCacheIdNoSDK}`);
      window.open(signUpUrlWithCacheIdNoSDK, '_blank');
    } else {
      logger.trace(`[${section}] Click sign up button. signUpUrl: ${signUpUrlWithCacheId}`);
      window.open(signUpUrlWithCacheId, '_self');
    }
  }), [isGoogleAuthEnabled, isFacebookAuthEnabled, isSDKMode, logger, cacheId, signUpUrlWithCacheId]);

  const onClickLogIn = useCallback(() => {
    if (isGoogleAuthNewSignInPageEnabled || isFacebookAuthNewSignInPageEnabled) {
      // Go to login idp selection page
      logger.trace('[Header] Click login button to go to login idp selection page.');
      const path = `/campaign/pmaxlitelanding/signin${location.search}`;
      const pathWithCacheId = appendCacheId(path, cacheId);
      window.open(pathWithCacheId, '_self');
    } else if (isSDKMode) {
      const loginUrlNoSDK = removeQueryParam('isSDK', loginUrl);
      logger.trace(`[SDK][Header] Click login button. loginUrl: ${loginUrlNoSDK}`);
      window.open(loginUrlNoSDK, '_blank');
    } else {
      logger.trace(`[Header] Click login button. loginUrl: ${loginUrl}`);
      window.open(loginUrl, '_self');
    }
  }, [isGoogleAuthNewSignInPageEnabled, isFacebookAuthNewSignInPageEnabled,
    isSDKMode, logger, loginUrl, cacheId]);

  const onRejectLiveChat = useCallback(() => {
    logger.trace('[LiveChat] Reject live chat');
  }, [logger]);

  const onStartLiveChat = useCallback(() => {
    if (liveChatSrc) {
      logger.trace('[LiveChat] Start live chat');
      const popupWindowFeatures = getPopupWindowFeatures();
      window.open(liveChatSrc, 'Virtual Agent Chat', popupWindowFeatures);
    }
  }, [logger]);

  return (
    <div className="pmax-lite-landing-page">
      {
        !isSDKMode &&
        <LandingPageHeader
          onClickSignUp={getOnClickSignUpFn('Header')}
          onClickLogIn={onClickLogIn}
        />
      }
      <LandingPageCover
        onClickSignUp={getOnClickSignUpFn('Cover')}
        onClickClaimCoupon={getOnClickSignUpFn('Coupon')}
        signUpUrlWithCacheId={signUpUrlWithCacheId}
        setCacheId={setCacheId}
        setShowAdPreviewPlaceholder={setShowAdPreviewPlaceholder}
        websiteInputRef={websiteInputRef}
        methodsContainerRef={methodsContainerRef}
        logger={logger}
      />
      <DelayedAdPreviewPlaceholder
        showAdPreviewPlaceholder={showAdPreviewPlaceholder}
        websiteInputRef={websiteInputRef}
        methodsContainerRef={methodsContainerRef}
        logger={logger}
      />
      <DelayedLandingPageFeature
        onClickSignUp={getOnClickSignUpFn('Feature')}
        logger={logger}
      />
      <DelayedLandingPageCost logger={logger} />
      <DelayedLandingPageFAQ logger={logger} />
      <LandingPageFooter logger={logger} />
      {liveChatSrc && <DelayedChatEntrance
        i18n={i18n}
        onStartLiveChat={onStartLiveChat}
        onRejectLiveChat={onRejectLiveChat}
      />}
    </div>
  );
}

PMaxLiteLandingPageCore.propTypes = {
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

PMaxLiteLandingPageCore.defaultProps = {
  logger: {
    trace: noop,
  },
};

